import { addDays } from '../engine/civil'
import type { IsoDate, ShiftCode } from '../engine/types'
import type { FeedFilters as FeedFiltersState } from '../lib/feed'
import { SegmentedControl, Toggle } from '../ui'
import type { SegmentedOption } from '../ui'

export interface FeedFiltersProps {
  /** Writes back into feed.ts's filter shape — the state stays with the screen. */
  value: FeedFiltersState
  onChange: (next: FeedFiltersState) => void
  /** Anchors the range presets; injected so the control stays deterministic. */
  today: IsoDate
}

/** Range presets, widest first. `all` is capped at the roster's forward horizon — the fit verdict needs the viewer's roster to exist that far, and 8 weeks is the default. */
type RangePreset = '1w' | '4w' | '8w' | 'all'

const RANGE_OPTIONS: SegmentedOption<RangePreset>[] = [
  { value: '1w', label: '1 week' },
  { value: '4w', label: '4 weeks' },
  { value: '8w', label: '8 weeks' },
  { value: 'all', label: 'All' },
]

const CODE_OPTIONS: SegmentedOption<ShiftCode | 'ALL'>[] = [
  { value: 'ALL', label: 'All' },
  { value: 'AM', label: 'AM' },
  { value: 'D', label: 'D' },
  { value: 'PM', label: 'PM' },
  { value: 'N', label: 'N' },
]

/** `all` reaches to the roster's own forward reach — six months, `ACTAS_ROSTER.horizon`. */
const ALL_REACH_DAYS = 184

function rangeFor(preset: RangePreset, today: IsoDate): { from: IsoDate; to: IsoDate } {
  return {
    from: today,
    to: addDays(today, preset === '1w' ? 7 : preset === '4w' ? 28 : preset === '8w' ? 56 : ALL_REACH_DAYS),
  }
}

function presetFor(range: { from: IsoDate; to: IsoDate }, today: IsoDate): RangePreset {
  for (const preset of ['1w', '4w', '8w'] as const) {
    if (rangeFor(preset, today).to === range.to) return preset
  }
  return 'all'
}

/**
 * The feed's narrowing controls (plan §1 Client): shift code, date range,
 * hide-warnings. Each writes into `feed.ts`'s filter shape, so the narrowing
 * happens in the one tested place — this component only chooses values.
 */
export function FeedFilters({ value, onChange, today }: FeedFiltersProps) {
  return (
    <div className="ss-feed-filters">
      <SegmentedControl<ShiftCode | 'ALL'>
        label="Shift"
        options={CODE_OPTIONS}
        value={value.code}
        onChange={(code) => onChange({ ...value, code })}
        size="compact"
      />
      <SegmentedControl<RangePreset>
        label="Date range"
        options={RANGE_OPTIONS}
        value={presetFor(value.range, today)}
        onChange={(preset) => onChange({ ...value, range: rangeFor(preset, today) })}
        size="compact"
      />
      <Toggle
        label="Hide warning fits"
        checked={value.hideWarnings}
        onChange={(hideWarnings) => onChange({ ...value, hideWarnings })}
        description="Show only clean fits and the ones you can't work."
      />
    </div>
  )
}
